import { toNumberDisplayValue } from "@intentsify/utils";
import { barX, defineChart } from "@tanstack/charts";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { BrandChartTheme } from "../BrandChart";
import { BarChartItem } from "../BarChart";
import { truncateLabel } from "../BarChart/BarChart.definition";

/** Average glyph width of 12px Outfit tick labels. */
const AVERAGE_CHAR_WIDTH = 6.5;
const MIN_LABEL_CHARS = 12;

/** "42% Creative name": the rate leads so it lines up down the axis. */
export const formatCompletionLabel = (
  item: BarChartItem | undefined,
  label: string,
  maxChars: number,
) =>
  `${toNumberDisplayValue(item?.value ?? 0)}%  ${truncateLabel(
    label,
    maxChars,
  )}`;

/**
 * Horizontal bars of watched share per creative on a fixed 0–100% axis, so
 * creatives compare against the full video rather than each other.
 */
export const buildVideoCompletionRateDefinition = (
  data: BarChartItem[],
  brandTheme: BrandChartTheme,
  width: number,
) => {
  const byLabel = new Map(data.map((item) => [item.label, item]));
  const maxChars = Math.max(
    MIN_LABEL_CHARS,
    Math.floor((width * 0.4) / AVERAGE_CHAR_WIDTH),
  );

  return defineChart({
    theme: brandTheme.theme,
    marks: [
      barX(data, {
        x: "value",
        y: "label",
        key: "label",
        fill: brandTheme.primary,
        radius: { end: 3 },
      }),
    ],
    scales: {
      x: {
        // A configured instance keeps the fixed domain; factories re-infer it.
        scale: scaleLinear().domain([0, 100]),
        grid: true,
        axis: {
          line: false,
          ticks: {
            count: 3,
            size: 0,
            format: (value: number) => `${value}%`,
          },
        },
      },
      y: {
        scale: () => scaleBand<string>().paddingInner(0.2).paddingOuter(0),
        axis: {
          ticks: {
            size: 0,
            format: (label: string) =>
              formatCompletionLabel(byLabel.get(label), label, maxChars),
          },
        },
      },
    },
  });
};
